/**
 * Phase 7 POS connections (spec §2.4, D-INV-13): which invoice a sale can
 * become, the credit limit warning, and the collections line for close cash.
 * Pure, so it is unit tested without the dashboard.
 */
import type { CustomerBalance } from '../../internals/repository/InvoiceRepository';
import { money } from './format';

/** Payment methods a "Pay later" (on account) sale is stored with. */
export const ON_ACCOUNT_METHODS = ['PAY_LATER', 'ON_ACCOUNT'];

export const isOnAccountSale = (order: { paymentMethod?: string; payment?: string } | null | undefined) =>
  ON_ACCOUNT_METHODS.includes(String(order?.paymentMethod ?? order?.payment ?? '').toUpperCase().replace(/[\s-]/g, '_'));

/** A paid sale becomes a PAID invoice-receipt; a sale on account becomes an issued invoice. */
export const modeForOrder = (order: { paymentMethod?: string; payment?: string } | null | undefined) =>
  (isOnAccountSale(order) ? 'ON_ACCOUNT' : 'PAID') as 'PAID' | 'ON_ACCOUNT';

const COMPLETED = ['COMPLETED', 'FULLY_PAID'];
/** Orders without a status (older list rows) are treated as completed; the server checks again. */
export const canSendAsInvoice = (order: { orderStatus?: string; status?: string } | null | undefined) => {
  const s = String(order?.orderStatus ?? order?.status ?? '').toUpperCase();
  return !s || COMPLETED.includes(s);
};

/** `null` when within the limit or no limit; otherwise the warning text. */
export function creditWarning(b: Pick<CustomerBalance, 'outstandingMinor' | 'creditLimitMinor'> | null | undefined, currency = 'LKR', extraMinor = 0): string | null {
  if (!b || b.creditLimitMinor === null || b.creditLimitMinor === undefined) return null;
  const owed = b.outstandingMinor + extraMinor;
  if (owed <= b.creditLimitMinor) return null;
  return `Over the credit limit: ${money(owed, currency)} owed against a limit of ${money(b.creditLimitMinor, currency)}.`;
}

export const METHOD_LABEL: Record<string, string> = {
  CASH: 'Cash', CARD: 'Card', BANK_TRANSFER: 'Bank transfer', CHEQUE: 'Cheque', LANKAQR: 'LankaQR', OTHER: 'Other',
};
export const SOURCE_LABEL: Record<string, string> = {
  POS: 'At the counter', STAFF: 'Recorded by staff', CUSTOMER: 'Reported by customer', GATEWAY: 'Card online',
};

/** Rows for a by-method or by-source breakdown, largest first, labelled. */
export function breakdown(map: Record<string, number> | undefined, labels: Record<string, string>) {
  return Object.entries(map || {})
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([key, amountMinor]) => ({ key, label: labels[key] || key, amountMinor }));
}

/** Rupees as the POS stores them → minor units. */
export const toMinor = (v: unknown) => {
  const n = typeof v === 'string' ? parseFloat(v) : (v as number);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};
